import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    ChevronDown, Target, Mail, Megaphone, Linkedin, Search, Rss, Video, Instagram, FileText, PlusCircle, Trash2,
    Calendar, Download, CheckCircle, Info, Lightbulb, Sparkles, Loader2, AlertTriangle, BrainCircuit,
    Rocket, Edit, Facebook, LayoutDashboard, Layers, PenSquare, Users, SearchCode, Settings,
    ArrowLeft, ArrowRight, Save, Video as WebinarIcon, LogOut, UploadCloud, BarChart2, X, Globe
} from 'lucide-react';
import apiService from '../../services/apiService';
import { PageLoader } from '../../components/PageLoader';
import Modal from '../../components/Modal';
import { DynamicCreatorForm } from '../foundry/DynamicCreatorForm';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://martechai-backend.onrender.com';

// --- NEW: Centralized Channel Configuration ---
// This is now the single source of truth for channel metadata.
const channelConfig = {
    'emails': { name: 'Email', icon: <Mail />, borderColor: 'border-t-orange-500', textColor: 'text-orange-400' },
    'webinars': { name: 'Webinar', icon: <WebinarIcon />, borderColor: 'border-t-red-500', textColor: 'text-red-400' },
    'wordpress-blog': { name: 'Blog Article', icon: <Rss />, borderColor: 'border-t-teal-500', textColor: 'text-teal-400' },
    'facebook-post': { name: 'Facebook Post', icon: <Facebook />, borderColor: 'border-t-blue-600', textColor: 'text-blue-400' },
    'linkedin-post': { name: 'LinkedIn Post', icon: <Linkedin />, borderColor: 'border-t-sky-500', textColor: 'text-sky-400' },
    'google-ad': { name: 'Google Ad', icon: <FileText />, borderColor: 'border-t-yellow-500', textColor: 'text-yellow-400' },
    'linkedin-ad': { name: 'LinkedIn Ad', icon: <FileText />, borderColor: 'border-t-sky-500', textColor: 'text-sky-400' },
    'facebook-ad': { name: 'Facebook Ad', icon: <FileText />, borderColor: 'border-t-blue-600', textColor: 'text-blue-400' },
    'instagram-ad': { name: 'Instagram Ad', icon: <Instagram />, borderColor: 'border-t-pink-500', textColor: 'text-pink-400' },
    'default': { name: 'Task', icon: <FileText />, borderColor: 'border-t-slate-700', textColor: 'text-slate-400' }
};

// Helper to format dates specifically for the export template's JS
const formatDateForExport = (dateStr) => {
    if (!dateStr) return 'Unscheduled';
    return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

// Helper to map your channel IDs to the simple 'Type' the template's JS expects
const mapChannelToType = (channelId) => {
    if (channelId.includes('email')) return 'Email';
    if (channelId.includes('blog')) return 'Blog';
    if (channelId.includes('ad')) return 'Ad';
    if (channelId.includes('post')) return 'Social';
    return 'Email'; // Default
};


// --- REPLACE your old `generateCampaignReportHTML` function with this new one ---
// --- REPLACE your old `generateCampaignReportHTML` function with this new one ---
// --- REPLACE your old `generateCampaignReportHTML` function with this new one ---
// --- REPLACE your old `generateCampaignReportHTML` function with this new one ---
const generateCampaignReportHTML = (campaign, channelConfig) => {
    // Helper function to generate the insights HTML block
    const generateInsightsHTML = (campaign) => {
        if (!campaign.insights || (!campaign.insights.ctaRecommendations?.length && !campaign.insights.campaignAdditions?.length)) {
            return ''; // Return nothing if there are no insights
        }

        const brainIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5a3 3 0 1 0-5.993.142"/><path d="M18 13a3 3 0 1 0-5.993.142"/><path d="M21 8a3 3 0 1 0-5.993.142"/><path d="M6 18a3 3 0 1 0-5.993.142"/><path d="M14.5 15.5a3 3 0 1 0-5.993.142"/><path d="M12 5a3 3 0 0 0 5.993-.142"/><path d="M18 13a3 3 0 0 0 5.993-.142"/><path d="M21 8a3 3 0 0 0 5.993-.142"/><path d="M6 18a3 3 0 0 0 5.993-.142"/><path d="M14.5 15.5a3 3 0 0 0 5.993-.142"/><path d="M12 5h.01"/><path d="M18 13h.01"/><path d="M21 8h.01"/><path d="M6 18h.01"/><path d="M14.5 15.5h.01"/><path d="m14.5 18.5 4-3"/><path d="m12 8 6 3"/><path d="M12 2v.01"/><path d="m4.007 16.858 2-3"/><path d="m6.01 15-4.007-3.142"/></svg>';

        const ctaHtml = (campaign.insights.ctaRecommendations || []).map(cta => `
            <div class="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                <a href="${cta.url}" target="_blank" rel="noopener noreferrer" class="font-bold text-slate-100 hover:text-purple-400 transition-colors">${cta.title}</a>
                <div class="text-sm text-slate-300 border-l-4 border-purple-500 pl-3 mt-2">
                    <strong class="font-semibold text-purple-400">Justification:</strong> ${cta.justification}
                </div>
            </div>
        `).join('');

        const additionsHtml = (campaign.insights.campaignAdditions || []).map(rec => `
             <div class="bg-slate-800/50 p-4 rounded-lg border-l-4 border-cyan-500">
                <h5 class="font-bold text-md text-cyan-400 mb-2">${rec.type}</h5>
                <p class="mb-2 text-slate-300"><strong>Recommendation:</strong> ${rec.recommendation}</p>
                <p class="text-slate-400"><strong>Focus:</strong> ${rec.focus}</p>
            </div>
        `).join('');

        return `
            <div class="mt-6 bg-[#111827] p-6 rounded-xl">
                <h3 class="text-xl font-semibold text-white mb-6 flex items-center gap-3">${brainIcon} AI-Generated Insights</h3>
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    ${ctaHtml ? `<div><h4 class="text-lg font-semibold text-slate-200 mb-4">Top CTA Recommendations</h4><div class="space-y-4">${ctaHtml}</div></div>` : ''}
                    ${additionsHtml ? `<div><h4 class="text-lg font-semibold text-slate-200 mb-4">Strategic Campaign Additions</h4><div class="space-y-4">${additionsHtml}</div></div>` : ''}
                </div>
            </div>
        `;
    };

    // 1. Transform timeline and content data (this part is unchanged)
    const timelineDataForExport = (campaign.timeline || []).map(item => ({
        week: item.week,
        date: formatDateForExport(item.scheduledDate),
        title: item.action,
        description: `Content for the "${item.channel}" channel.`,
        type: mapChannelToType(item.channelId),
        channelId: item.channelId,
        taskType: item.taskType,
    }));

    const contentDataForExport = (campaign.timeline || [])
        .filter(item => item.taskType === 'Content' && item.details && Object.keys(item.details).length > 0)
        .map(item => {
            if (typeof item.details === 'string') {
                return { title: item.action, channel: item.channel, details: item.details.replace(/\\/g, '\\\\').replace(/`/g, '\\`').replace(/\$\{/g, '\\${') };
            }
            const escapedDetails = {};
            for (const [key, value] of Object.entries(item.details)) {
                if (typeof value === 'string') {
                    escapedDetails[key] = value.replace(/\\/g, '\\\\').replace(/`/g, '\\`').replace(/\$\{/g, '\\${');
                } else {
                    escapedDetails[key] = value;
                }
            }
            return { title: item.action, channel: item.channel, details: escapedDetails };
        });

    // 2. Return the full HTML template with your data injected
    return `
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Campaign Dashboard | ${campaign.name}</title>
    <script src="https://cdn.tailwindcss.com"></script>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>
        body { font-family: 'Inter', sans-serif; background-color: #0B1120; color: #94a3b8; }
        .sidebar { scrollbar-width: none; }
        .sidebar::-webkit-scrollbar { display: none; }
        .content-section { display: none; }
        .content-section.active { display: block; }
        .nav-link.active { background-color: #1E293B; color: #f1f5f9; }
        .content-item-body { display: none; }
        .prose a { color: #818cf8; }
        .prose a:hover { color: #a78bfa; }
        .prose strong, .prose-sm strong { color: #e2e8f0; }
        .prose-sm a { color: #818cf8; }
        .prose-sm a:hover { color: #a78bfa; }
    </style>
</head>
<body class="flex h-screen">

    <aside class="w-64 bg-[#111827] text-slate-300 p-6 flex flex-col justify-between sidebar overflow-y-auto">
        <div>
            <div class="flex items-center gap-3 mb-10">
                <div class="bg-indigo-600 p-2 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-white"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="m9 12 2 2 4-4"/></svg>
                </div>
                <h2 class="text-xl font-bold text-white">Campaigns</h2>
            </div>
            <nav>
                <ul>
                    <li><a href="#" class="nav-link flex items-center gap-3 py-3 px-4 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors" data-section="dashboard">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></svg>
                        Dashboard
                    </a></li>
                    <li><a href="#" class="nav-link flex items-center gap-3 py-3 px-4 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors" data-section="timeline">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
                        Timeline
                    </a></li>
                    <li><a href="#" class="nav-link flex items-center gap-3 py-3 px-4 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors" data-section="content">
                         <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                        Generated Content
                    </a></li>
                </ul>
            </nav>
        </div>
        <div class="bg-slate-800 p-4 rounded-lg text-center">
            <p class="text-sm">Need help?</p>
            <a href="#" class="text-sm font-semibold text-indigo-400 hover:text-indigo-300">Contact Support</a>
        </div>
    </aside>
    <main class="flex-1 p-6 lg:p-10 overflow-y-auto">
        <header class="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
            <div>
                <h1 class="text-3xl font-bold text-white">Campaign Report</h1>
                <p class="text-slate-400 mt-1">${campaign.name}</p>
            </div>
        </header>
        <section id="dashboard" class="content-section">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                 <div class="bg-[#111827] p-6 rounded-xl">
                    <h3 class="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                       <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#facc15" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/></svg>
                        Campaign Goal
                    </h3>
                    <p class="text-sm leading-relaxed">${campaign.description || 'No goal specified.'}</p>
                </div>
                <div class="bg-[#111827] p-6 rounded-xl">
                    <h3 class="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                       <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#34d399" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                        Targeting
                    </h3>
                     <div class="space-y-4">
                        <div>
                            <p class="text-xs text-slate-400 uppercase font-bold">Product/Service</p>
                            <p class="text-white font-medium">${campaign.product || 'N/A'}</p>
                        </div>
                        <div>
                            <p class="text-xs text-slate-400 uppercase font-bold">Ideal Customer Profile</p>
                            <p class="text-white font-medium">${campaign.icp || 'N/A'}</p>
                        </div>
                         <div>
                            <p class="text-xs text-slate-400 uppercase font-bold mb-2">Buying Committee</p>
                            <div class="text-sm space-y-1">
                                <p><strong class="text-slate-300">Decision Makers:</strong> ${campaign.buyingCommittee?.decisionMakers || 'N/A'}</p>
                                <p><strong class="text-slate-300">Influencers:</strong> ${campaign.buyingCommittee?.influencers || 'N/A'}</p>
                                <p><strong class="text-slate-300">Users:</strong> ${campaign.buyingCommittee?.users || 'N/A'}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="bg-[#111827] p-6 rounded-xl">
                    <h3 class="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                       <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f472b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6"><line x1="12" x2="12" y1="20" y2="10"/><line x1="18" x2="18" y1="20" y2="4"/><line x1="6" x2="6" y1="20" y2="16"/></svg>
                        Key Metrics
                    </h3>
                    <ul class="space-y-3 text-sm">
                        <li class="flex justify-between"><span>Start Date:</span> <strong class="text-white">${formatDateForExport(campaign.startDate)}</strong></li>
                        <li class="flex justify-between"><span>Duration:</span> <strong class="text-white">${campaign.duration} Weeks</strong></li>
                        <li class="flex justify-between"><span>Primary KPI:</span> <strong class="text-white">${campaign.kpis?.primary || 'N/A'}</strong></li>
                        <li class="flex justify-between"><span>Secondary KPI:</span> <strong class="text-white">${campaign.kpis?.secondary || 'N/A'}</strong></li>
                    </ul>
                </div>
                <div class="bg-[#111827] p-6 rounded-xl">
                    <h3 class="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                       <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6"><path d="m3 11 18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>
                        Channel Mix
                    </h3>
                    <div class="flex flex-wrap gap-2">
                        ${(campaign.selectedChannels || []).map(channel => `<span class="bg-slate-700 text-slate-300 text-xs font-medium px-3 py-1 rounded-full">${channel.name}</span>`).join('')}
                    </div>
                </div>
            </div>
            
            ${generateInsightsHTML(campaign)}
        </section>
        
        <section id="timeline" class="content-section">
             <div class="bg-[#111827] p-6 rounded-xl">
                <h3 class="text-xl font-semibold text-white mb-6">Campaign Timeline</h3>
                <div id="timeline-container" class="space-y-8"></div>
            </div>
        </section>

        <section id="content" class="content-section">
            <div class="space-y-4" id="content-items-container"></div>
        </section>
    </main>

    <script>
        document.addEventListener('DOMContentLoaded', function () {
            const timelineData = ${JSON.stringify(timelineDataForExport)};
            const contentData = ${JSON.stringify(contentDataForExport)};
            const campaignDuration = ${campaign.duration || 4};
            
            const channelConfig = {
                'emails': { icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>', borderColor: 'border-t-orange-500', textColor: 'text-orange-400' },
                'webinars': { icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>', borderColor: 'border-t-red-500', textColor: 'text-red-400' },
                'wordpress-blog': { icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h4"/><path d="M16 2v20"/></svg>', borderColor: 'border-t-teal-500', textColor: 'text-teal-400' },
                'facebook-post': { icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>', borderColor: 'border-t-blue-600', textColor: 'text-blue-400' },
                'linkedin-post': { icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/></svg>', borderColor: 'border-t-sky-500', textColor: 'text-sky-400' },
                'google-ad': { icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.89 1.453 11.11 3.23a1 1 0 0 0 0 1.414l5.657 5.657a1 1 0 0 0 1.414 0L20.55 8.94a1 1 0 0 0 0-1.414zM4.18 10.395c-1.334-3.52.26-7.352 3.456-9.623 3.196-2.27 7.551-2.035 10.45 1.188"/><path d="M12.89 1.453 11.11 3.23a1 1 0 0 0 0 1.414l5.657 5.657a1 1 0 0 0 1.414 0L20.55 8.94a1 1 0 0 0 0-1.414z"/><path d="m14.28 15.08-5.657-5.657a1 1 0 0 0-1.414 0L3.45 13.18a1 1 0 0 0 0 1.414l5.657 5.657a1 1 0 0 0 1.414 0z"/><path d="M14.28 15.08 11.1 12.55a1 1 0 0 0-1.414 0L3.45 18.78a1 1 0 0 0 0 1.414l1.768 1.768a1 1 0 0 0 1.414 0l6.22-6.22a1 1 0 0 0 0-1.414z"/></svg>', borderColor: 'border-t-yellow-500', textColor: 'text-yellow-400' },
                'linkedin-ad': { icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.89 1.453 11.11 3.23a1 1 0 0 0 0 1.414l5.657 5.657a1 1 0 0 0 1.414 0L20.55 8.94a1 1 0 0 0 0-1.414zM4.18 10.395c-1.334-3.52.26-7.352 3.456-9.623 3.196-2.27 7.551-2.035 10.45 1.188"/><path d="M12.89 1.453 11.11 3.23a1 1 0 0 0 0 1.414l5.657 5.657a1 1 0 0 0 1.414 0L20.55 8.94a1 1 0 0 0 0-1.414z"/><path d="m14.28 15.08-5.657-5.657a1 1 0 0 0-1.414 0L3.45 13.18a1 1 0 0 0 0 1.414l5.657 5.657a1 1 0 0 0 1.414 0z"/><path d="M14.28 15.08 11.1 12.55a1 1 0 0 0-1.414 0L3.45 18.78a1 1 0 0 0 0 1.414l1.768 1.768a1 1 0 0 0 1.414 0l6.22-6.22a1 1 0 0 0 0-1.414z"/></svg>', borderColor: 'border-t-sky-500', textColor: 'text-sky-400' },
                'default': { icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>', borderColor: 'border-t-slate-700', textColor: 'text-slate-400' }
            };

            function renderTimeline() {
                const timelineContainer = document.getElementById('timeline-container');
                if (!timelineContainer) return;
                timelineContainer.innerHTML = '';

                const groupedByWeek = timelineData.reduce((acc, item) => {
                    const week = item.week || 1;
                    if (!acc[week]) acc[week] = [];
                    acc[week].push(item);
                    return acc;
                }, {});

                for (let i = 1; i <= campaignDuration; i++) {
                    const weekNum = i;
                    const items = groupedByWeek[weekNum] || [];
                    if (items.length === 0) continue;

                    let itemsHtml = '';
                    items.forEach(item => {
                        const meta = channelConfig[item.channelId] || channelConfig.default;
                        const isOperational = item.taskType === 'Operational';
                        const borderColorClass = isOperational ? 'border-t-slate-500' : meta.borderColor;
                        const textColorClass = isOperational ? 'text-slate-400' : meta.textColor;
                        const icon = isOperational ? '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 0 2l-.15.08a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.38a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1 0-2l.15-.08a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>' : meta.icon;

                        itemsHtml += \`
                            <div class="w-72 flex-shrink-0 bg-slate-900/70 rounded-lg p-3 flex flex-col border border-slate-700/50 border-t-4 \${borderColorClass}">
                                <div class="flex-grow">
                                    <div class="flex items-start gap-3">
                                        <div class="mt-1 \${textColorClass}">\${icon}</div>
                                        <div>
                                            <p class="font-bold text-white text-sm leading-tight break-words">\${item.title}</p>
                                            <p class="text-xs text-slate-400 mt-1">\${item.date}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        \`;
                    });

                    timelineContainer.innerHTML += \`
                        <div>
                            <h3 class="text-xl font-bold text-slate-200 mb-4">Week \${weekNum}</h3>
                            <div class="flex flex-wrap gap-4">\${itemsHtml}</div>
                        </div>
                    \`;
                }
            }

            // (The rest of the script is unchanged)
            function renderContent() {
                const contentContainer = document.getElementById('content-items-container');
                if (!contentContainer) return;
                contentContainer.innerHTML = '';
                const channelColors = { 'Email': 'bg-indigo-500/50 text-indigo-300', 'Blog Article': 'bg-sky-500/50 text-sky-300', 'LinkedIn Ad': 'bg-rose-500/50 text-rose-300', 'Google Ad': 'bg-amber-500/50 text-amber-300', 'Facebook Post': 'bg-blue-500/50 text-blue-300', 'LinkedIn Post': 'bg-emerald-500/50 text-emerald-300', };
                contentData.forEach(item => {
                    const colorClass = channelColors[item.channel] || 'bg-slate-500/50 text-slate-300';
                    let detailsHtml = '';
                    if (typeof item.details === 'string') {
                        detailsHtml = \`<div>\${item.details}</div>\`;
                    } else if (typeof item.details === 'object' && item.details !== null) {
                        detailsHtml = '<div class="prose prose-sm prose-invert max-w-none bg-slate-900/50 p-4 rounded-lg">';
                        for (const [key, value] of Object.entries(item.details)) {
                            detailsHtml += \`<div class="mb-4"><strong>\${key.replace(/([A-Z])/g, ' \$1').replace(/^./, str => str.toUpperCase())}:</strong><div>\${value}</div></div>\`;
                        }
                        detailsHtml += '</div>';
                    }
                    contentContainer.innerHTML += \`
                        <div class="bg-[#111827] rounded-xl overflow-hidden">
                            <button class="content-item-header w-full p-4 text-left flex justify-between items-center bg-slate-800 hover:bg-slate-700 transition-colors">
                                <h3 class="font-semibold text-white">\${item.title}</h3>
                                <div class="flex items-center gap-2">
                                    <span class="text-xs \${colorClass} px-2 py-1 rounded-full">\${item.channel}</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="chevron-icon transition-transform"><polyline points="6 9 12 15 18 9"></polyline></svg>
                                </div>
                            </button>
                            <div class="content-item-body p-6 text-sm">\${detailsHtml}</div>
                        </div>\`;
                });
                addContentToggleListeners();
            }
            const navLinks = document.querySelectorAll('.nav-link');
            const sections = document.querySelectorAll('.content-section');
            navLinks.forEach(link => { link.addEventListener('click', (e) => { e.preventDefault(); navLinks.forEach(l => l.classList.remove('active')); link.classList.add('active'); const sectionId = link.getAttribute('data-section'); sections.forEach(section => section.classList.toggle('active', section.id === sectionId)); }); });
            function addContentToggleListeners() { document.querySelectorAll('.content-item-header').forEach(header => { header.addEventListener('click', () => { const body = header.nextElementSibling; const icon = header.querySelector('.chevron-icon'); if (!body) return; const isVisible = body.style.display === 'block'; body.style.display = isVisible ? 'none' : 'block'; icon.style.transform = isVisible ? 'rotate(0deg)' : 'rotate(180deg)'; }); }); }
            document.querySelector('.nav-link[data-section="dashboard"]').classList.add('active');
            document.getElementById('dashboard').classList.add('active');
            renderTimeline();
            renderContent();
        });
    </script>
</body>
</html>`;
};

// --- Reusable UI Components ---
const Card = ({ children, className = '' }) => (
    <div className={`bg-slate-800 border border-slate-700 rounded-lg shadow-md ${className}`}>
        {children}
    </div>
);
const Toast = ({ message, onDone }) => {
    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => onDone(), 3000);
            return () => clearTimeout(timer);
        }
    }, [message, onDone]);
    if (!message) return null;
    return (
        <div className="fixed top-5 right-5 bg-emerald-500 text-white py-3 px-6 rounded-lg shadow-lg z-50 animate-fade-in-down">
            <div className="flex items-center gap-3">
                <CheckCircle size={20} />
                <span className="font-semibold">{message}</span>
            </div>
        </div>
    );
};
// --- NEW Confirmation Modal ---
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
        <div className="text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
            <p className="mt-4 text-slate-300">{message}</p>
        </div>
        <div className="mt-6 flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={onConfirm}>Confirm</Button>
        </div>
    </Modal>
);
const ImageUploadField = ({ label, value, onUpload, isUploading }) => {
    const fileInputRef = useRef(null);
    const fullImageUrl = value ? `${API_BASE_URL}/api/foundry/content-assets/${value}` : null;

    return (
        <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{label}</label>
            <div className="flex items-center gap-4">
                {value && (
                    <div className="w-24 h-24 rounded-lg bg-slate-800 flex-shrink-0">
                        <img src={fullImageUrl} alt="Preview" className="w-full h-full object-cover rounded-lg" />
                    </div>
                )}
                <div className="flex-grow">
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={(e) => onUpload(e.target.files[0])}
                        className="hidden"
                        accept="image/*"
                    />
                    <Button 
                        onClick={() => fileInputRef.current.click()} 
                        variant="secondary"
                        icon={isUploading ? Loader2 : UploadCloud}
                        disabled={isUploading}
                        className={isUploading ? 'animate-spin' : ''}
                    >
                        {isUploading ? 'Uploading...' : 'Upload Image'}
                    </Button>
                    {value && <p className="text-xs text-slate-500 mt-2">Filename: {value}</p>}
                </div>
            </div>
        </div>
    );
};
// --- THIS IS THE FIX for the silent crash and state loss ---
const Button = ({ children, onClick, className = '', variant = 'primary', icon: Icon, disabled = false, ...props }) => {
    const baseClasses = 'flex items-center justify-center gap-2 px-4 py-2 rounded-md font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
    const variants = {
        primary: 'bg-brand-500 text-white hover:bg-brand-400',
        secondary: 'bg-slate-700 text-slate-200 hover:bg-slate-600',
        success: 'bg-emerald-600 text-white hover:bg-emerald-700',
        // The 'ghost' variant used by the "Previous" button was missing.
        ghost: 'bg-transparent text-slate-400 hover:bg-slate-700/50 hover:text-slate-200'
    };
    return (
        <button onClick={onClick} disabled={disabled} className={`${baseClasses} ${variants[variant]} ${className}`} {...props}>
            {Icon && <Icon size={16} />}
            {children}
        </button>
    );
};

// --- Dashboard Component ---
const CampaignDashboard = ({ campaigns, onEdit, onDelete, onCreateNew }) => (
    <div className="animate-fade-in">
        <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl font-bold text-white">Campaigns Dashboard</h2>
            <Button onClick={onCreateNew} icon={Sparkles} variant="primary">Create New Campaign</Button>
        </div>
        {campaigns.length === 0 ? (
            <div className="text-center p-12 bg-slate-900/50 rounded-2xl">
                <h3 className="text-2xl font-semibold text-white mb-2">No Campaigns Yet</h3>
                <p className="text-slate-400 mb-6">Click "Create New Campaign" to get started with the builder.</p>
                <Rocket className="mx-auto h-16 w-16 text-purple-400" />
            </div>
        ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {campaigns.map(c => (
                    <div key={c._id} className="group relative bg-slate-900/80 rounded-2xl p-6 flex flex-col justify-between border border-slate-800 hover:border-brand-600/50 transition-all duration-300 hover:-translate-y-1">
                        <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-brand-700 to-cyan-700 opacity-0 group-hover:opacity-30 transition-opacity duration-300 blur-xl"></div>
                        <div className="relative">
                            <h3 className="text-xl font-bold text-white mb-2 truncate">{c.name}</h3>
                            <p className="text-sm text-slate-400 mb-4 h-10">Goal: {(c.description || '').substring(0, 100)}...</p>
                             <div className="flex items-center justify-between">
                                <span className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${c.status === 'Active' ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-300'}`}>
                                    {c.status}
                                </span>
                                <p className="text-xs text-slate-500">Updated {new Date(c.updatedAt).toLocaleDateString()}</p>
                            </div>
                        </div>
                        <div className="relative flex items-center justify-end space-x-2 mt-4 border-t border-slate-700/50 pt-4">
                            <button onClick={() => onDelete(c._id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={18} /></button>
                            <Button onClick={() => onEdit(c)} variant="secondary" icon={Edit}>Edit</Button>
                        </div>
                    </div>
                ))}
            </div>
        )}
    </div>
);


// --- Main Builder Component ---
const CampaignBuilder = ({ initialCampaign, allCampaigns, onExit, setToastMessage, setConfirmation }) => {
    const [step, setStep] = useState(1);
    const [brandHub, setBrandHub] = useState(null);
    const [isLoadingBrandHub, setIsLoadingBrandHub] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [insights, setInsights] = useState(initialCampaign?.insights || null);
    const [isDeployingToGoodDay, setIsDeployingToGoodDay] = useState(false);
    const [isGoodDayConnected, setIsGoodDayConnected] = useState(false);
    const [campaign, setCampaign] = useState(() => {
        const defaults = {
            name: 'New Campaign',
            description: '',
            icp: '',
            product: [], // Now an array
            region: 'All', // NEW
            startDate: Date.now(),
            duration: 4,
            selectedChannels: [],
            timeline: [],
            kpis: { primary: 'Pipeline Generated', secondary: 'Engagement Score Increase' },
            buyingCommittee: { decisionMakers: '', influencers: '', users: '' },
        };
        const merged = { ...defaults, ...initialCampaign };
        // Ensure 'product' is always an array, even when loading an existing campaign
        if (typeof merged.product === 'string') {
            merged.product = [merged.product];
        }
        if (initialCampaign?.buyingCommittee) {
            merged.buyingCommittee = { ...defaults.buyingCommittee, ...initialCampaign.buyingCommittee };
        }
        return merged;
    });

    useEffect(() => {
        let isMounted = true;
        const fetchInitialData = async () => {
            setIsLoadingBrandHub(true);
            try {
                const brandHubData = await apiService.fetchBrandHub();
                if (isMounted) {
                    setBrandHub(brandHubData);
                    // This logic now correctly handles setting defaults for a NEW campaign
                    if (!initialCampaign) {
                        setCampaign(c => ({
                            ...c,
                            icp: c.icp || brandHubData?.customerProfiles?.[0]?.name || '',
                            // --- THIS IS THE FIX ---
                            // It ensures the default product is set as an array, not a string.
                            product: (c.product && c.product.length > 0) ? c.product : (brandHubData?.products?.[0]?.title ? [brandHubData.products[0].title] : [])
                        }));
                    }
                }
            } catch (error) {
                console.error("Failed to fetch initial builder data", error);
            } finally {
                if (isMounted) { setIsLoadingBrandHub(false); }
            }
        };
        fetchInitialData();
        return () => { isMounted = false; };
    }, [initialCampaign]);
    // --- NEW: Check GoodDay connection status on load ---
    useEffect(() => {
    let mounted = true;
    (async () => {
        try {
        const connections = await apiService.fetchConnections();
        const goodDay =
            connections?.find(
            c =>
                c?.id === 'goodday' ||
                c?.provider === 'goodday' ||
                (typeof c?.name === 'string' && c.name.toLowerCase() === 'goodday')
            ) || null;

        if (mounted && goodDay && (goodDay.connected || goodDay.isConnected === true)) {
            setIsGoodDayConnected(true);
        }
        } catch (err) {
        console.error('Failed to fetch connection statuses for Campaign Builder:', err);
        }
    })();
    return () => { mounted = false; };
    }, []);
    const handleSave = async (launch = false, exitAfter = true) => {
        setIsSaving(true);
        const campaignData = { ...campaign, insights, status: launch ? 'Active' : 'Draft' };
        try {
            let savedCampaign;
            if (campaign._id) {
                savedCampaign = await apiService.updateCampaign(campaign._id, campaignData);
            } else {
                savedCampaign = await apiService.createCampaign(campaignData);
            }
            setCampaign(savedCampaign); 
            
            if (launch) {
                await apiService.deployCampaign(savedCampaign._id);
                setToastMessage(`Campaign launched & tasks deployed!`);
            } else if (exitAfter) {
                setToastMessage(`Campaign draft saved!`);
            }
            if (exitAfter) onExit();
            return true;
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Could not save the campaign.";
            setToastMessage(`Error: ${errorMessage}`);
            return false;
        } finally {
            setIsSaving(false);
        }
    };
    // --- NEW: Push current campaign to GoodDay ---
    const handleDeployToGoodDay = async () => {
    if (!campaign?._id) {
        setToastMessage('Please save or launch the campaign first.');
        return;
    }
    setIsDeployingToGoodDay(true);
    try {
        const response = await apiService.deployCampaignToGoodDay(campaign._id);
        setToastMessage(response?.message || 'Deployment to GoodDay.work has been initiated.');
    } catch (error) {
        setToastMessage(error?.response?.data?.message || 'Failed to deploy to GoodDay.work.');
    } finally {
        setIsDeployingToGoodDay(false);
    }
    };
    const totalSteps = 7;
    const stepNames = ['Goal', 'Insights', 'Targeting', 'Channels', 'Planning', 'Content', 'Review'];
    
    const handleNextStep = async () => {
        const success = await handleSave(false, false);
        if (success) setStep(s => Math.min(s + 1, totalSteps));
    };
    const prevStep = () => setStep(s => Math.max(s - 1, 1));

    const renderStep = () => {
        const props = { campaign, setCampaign, brandHub, allCampaigns, setToastMessage, setConfirmation };
        if (isLoadingBrandHub) return <LoadingSpinner text="Loading Brand Compass..." />;
        switch (step) {
            case 1: return <Step1Goal {...props} />;
            case 2: return <Step2Insights {...props} insights={insights} setInsights={setInsights} />;
            case 3: return <Step3Targeting {...props} />;
            case 4: return <Step4Channels {...props} />;
            case 5: return <Step5TimelinePlanning {...props} />;
            case 6: return <Step6ContentGeneration {...props} />;
            case 7: return <Step7Review {...props} />;
            default: return <p>Error: Unknown step.</p>;
        }
    };
    
    return (
        <div className="animate-fade-in pb-20">
            <header className="mb-8 flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-slate-100">Campaign Builder</h2>
                    <p className="text-slate-400 mt-1">Step {step}: {stepNames[step-1]}</p>
                </div>
                <div className="flex items-center space-x-4">
                     <div className="flex items-center space-x-2">
                        <Button onClick={prevStep} disabled={step === 1} variant="ghost" icon={ArrowLeft}>Previous</Button>
                        {step < totalSteps ? (
                            <Button onClick={handleNextStep} icon={ArrowRight}>Next</Button>
                            ) : (
                            <div className="flex items-center gap-2">
                                <Button
                                onClick={() => handleSave(true, true)}
                                disabled={isSaving}
                                icon={Rocket}
                                >
                                {isSaving ? 'Launching...' : 'Save & Launch'}
                                </Button>

                                {isGoodDayConnected && (
                                <Button
                                    onClick={handleDeployToGoodDay}
                                    disabled={isDeployingToGoodDay}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                    icon={isDeployingToGoodDay ? Loader2 : UploadCloud}
                                >
                                    {isDeployingToGoodDay ? 'Sending...' : 'Push to GoodDay'}
                                </Button>
                                )}
                            </div>
                            )}
                    </div>
                    <div className="border-l border-slate-700 h-8"></div>
                    <div className="flex items-center space-x-2">
                        <Button onClick={() => handleSave(false, true)} disabled={isSaving} variant="secondary" icon={Save}>{isSaving ? 'Saving...' : 'Save & Exit'}</Button>
                        <Button onClick={onExit} variant="secondary">Exit</Button>
                    </div>
                </div>
            </header>
            <div className="mb-12">
                 <div className="relative">
                    <div className="absolute top-1/2 left-0 w-full h-1 bg-slate-800 rounded-full" style={{ transform: 'translateY(-50%)' }}></div>
                    <div className="absolute top-1/2 left-0 h-1 bg-gradient-to-r from-cyan-500 to-brand-500 rounded-full" style={{ width: `${((step - 1) / (totalSteps - 1)) * 100}%` }}></div>
                    <div className="relative flex justify-between items-start">
                        {stepNames.map((name, index) => {
                            const stepNumber = index + 1;
                            const isActive = step === stepNumber;
                            const isCompleted = step > stepNumber;
                            return (
                                <div key={name} className="z-10 flex flex-col items-center w-24">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${isCompleted ? 'bg-brand-500 text-white' : isActive ? 'bg-brand-500 text-white animate-pulse' : 'bg-slate-900 border-2 border-slate-700 text-slate-400'}`}>
                                        {isCompleted ? <CheckCircle size={24} /> : stepNumber}
                                    </div>
                                    <p className={`text-center mt-3 text-sm font-semibold transition-colors ${isActive || isCompleted ? 'text-brand-300' : 'text-slate-500'}`}>{name}</p>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
            <div className="bg-slate-900/50 rounded-2xl shadow-2xl p-6 sm:p-8">{renderStep()}</div>
        </div>
    );
};
// --- Helper & Step Components ---
const SectionTitle = ({ icon, title, children }) => (
    <div className="flex items-center justify-between text-xl font-bold text-slate-100 mb-4">
        <div className="flex items-center">
            {icon}
            <h2 className="ml-3">{title}</h2>
        </div>
        <div>{children}</div>
    </div>
);
const CustomTimelineScrollbar = ({ scrollWidth, clientWidth, scrollLeft, onScroll }) => {
    const trackRef = useRef(null);
    const thumbRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    // Calculate the width and position of the thumb
    const thumbWidth = useMemo(() => {
        if (!clientWidth || !scrollWidth) return 0;
        return Math.max(20, (clientWidth / scrollWidth) * 100); // % width, with a minimum size
    }, [clientWidth, scrollWidth]);

    const thumbPosition = useMemo(() => {
        if (!scrollWidth || !clientWidth) return 0;
        const scrollPercentage = scrollLeft / (scrollWidth - clientWidth);
        return scrollPercentage * (100 - thumbWidth); // % position from left
    }, [scrollLeft, scrollWidth, clientWidth, thumbWidth]);

    const handleTrackClick = (e) => {
        if (!trackRef.current || !thumbRef.current) return;
        const trackRect = trackRef.current.getBoundingClientRect();
        const thumbRect = thumbRef.current.getBoundingClientRect();
        
        // Click position relative to the track
        const clickX = e.clientX - trackRect.left;
        
        // Center the thumb on the click position
        const newThumbX = clickX - (thumbRect.width / 2);
        
        const scrollPercentage = newThumbX / trackRect.width;
        onScroll(scrollPercentage * scrollWidth);
    };
    
    // Logic for dragging the thumb
    const handleThumbMouseDown = useCallback((e) => {
        e.stopPropagation();
        setIsDragging(true);
        const startX = e.clientX;
        const startScrollLeft = scrollLeft;

        const handleMouseMove = (moveEvent) => {
            const dx = moveEvent.clientX - startX;
            const trackWidth = trackRef.current?.offsetWidth || 0;
            const scrollableWidth = scrollWidth - clientWidth;
            
            // Convert pixels dragged to scroll distance
            const scrollDelta = (dx / trackWidth) * scrollableWidth;
            onScroll(startScrollLeft + scrollDelta);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [scrollLeft, scrollWidth, clientWidth, onScroll]);


    if (scrollWidth <= clientWidth) {
        return null; // Don't show scrollbar if content doesn't overflow
    }

    return (
        <div 
            ref={trackRef}
            className="w-1/2 h-3 bg-slate-800 rounded-full cursor-pointer"
            onClick={handleTrackClick}
        >
            <div
                ref={thumbRef}
                className="h-3 bg-slate-600 rounded-full relative cursor-grab active:cursor-grabbing"
                style={{
                    width: `${thumbWidth}%`,
                    left: `${thumbPosition}%`,
                }}
                onMouseDown={handleThumbMouseDown}
            ></div>
        </div>
    );
};
const LoadingSpinner = ({ text }) => <div className="flex flex-col items-center justify-center p-8 text-slate-400"><Loader2 className="w-8 h-8 animate-spin mb-4" /><p>{text}</p></div>;
const ErrorDisplay = ({ message }) => <div className="flex flex-col items-center justify-center p-8 text-red-400 bg-red-900/30 rounded-lg"><AlertTriangle className="w-8 h-8 mb-4" /><p className="font-semibold">An Error Occurred</p><p className="text-sm">{message}</p></div>;

const SelectField = ({ label, name, value, onChange, options }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-slate-300 mb-2">{label}</label>
        <div className="relative">
            <select id={name} name={name} value={value} onChange={onChange} className="w-full appearance-none bg-slate-700 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-brand-500 outline-none">
                {options && options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
        </div>
    </div>
);
// --- NEW MultiSelectField for Products ---
const MultiSelectField = ({ label, name, selectedOptions, onChange, allOptions, icon: Icon }) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);
    
    const handleSelect = (option) => {
        let newSelected;
        if (selectedOptions.includes(option)) {
            newSelected = selectedOptions.filter(item => item !== option);
        } else {
            newSelected = [...selectedOptions, option];
        }
        onChange({ target: { name, value: newSelected } });
    };

    return (
        <div ref={wrapperRef}>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">{Icon && <Icon size={16}/>} {label}</label>
            <div className="relative">
                <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full text-left bg-slate-700 border border-slate-600 rounded-lg p-3 text-white flex justify-between items-center">
                    <span className="truncate">
                        {selectedOptions.length > 0 ? selectedOptions.join(', ') : 'Select products...'}
                    </span>
                    <ChevronDown className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-lg max-h-60 overflow-auto">
                        {allOptions.map(option => (
                            <div key={option} onClick={() => handleSelect(option)} className="px-4 py-2 hover:bg-slate-700 cursor-pointer flex items-center">
                                <input
                                    type="checkbox"
                                    checked={selectedOptions.includes(option)}
                                    readOnly
                                    className="h-4 w-4 rounded bg-slate-600 border-slate-500 text-brand-500 focus:ring-brand-500 mr-3"
                                />
                                {option}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
const Step1Goal = ({ campaign, setCampaign, brandHub, allCampaigns }) => {
    const [isSuggesting, setIsSuggesting] = useState(false);

    const formatDateForInput = (date) => new Date(date).toISOString().split('T')[0];
    const handleInputChange = (e) => setCampaign(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSuggestion = async () => {
        setIsSuggesting(true);
        try {
            const products = brandHub.products.filter(p => campaign.product.includes(p.title));
            const icp = brandHub.customerProfiles.find(p => p.name === campaign.icp);
            if (products.length === 0 || !icp) {
                alert("Please ensure at least one Product/Service and a valid ICP are selected.");
                return;
            }
            const existingGoals = allCampaigns.map(c => c.description).filter(Boolean);
            const { suggestion } = await apiService.generateCampaignGoalSuggestion({
                products,
                icp,
                existingGoals
            });
            setCampaign(prev => ({ ...prev, description: suggestion }));
        } catch (error) {
            alert("Failed to get AI suggestion.");
        } finally {
            setIsSuggesting(false);
        }
    };

    return (
        <div>
            <SectionTitle icon={<Lightbulb className="w-6 h-6 text-yellow-400" />} title="Define Your Campaign Goal" />
            <p className="mb-8 text-slate-400">Start by describing the main objective of your campaign.</p>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Campaign Name</label>
                        <input type="text" name="name" value={campaign.name} onChange={handleInputChange} className="w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white" />
                    </div>
                    
                    <MultiSelectField
                        label="Product(s) / Service(s)"
                        name="product"
                        selectedOptions={campaign.product || []}
                        onChange={handleInputChange}
                        allOptions={brandHub?.products?.map(p => p.title) || []}
                        icon={Layers}
                    />
                    
                    <SelectField 
                        label="Ideal Customer Profile (ICP)" 
                        name="icp" 
                        value={campaign.icp || ''} 
                        onChange={handleInputChange} 
                        options={brandHub?.customerProfiles?.map(p => p.name) || []} 
                        icon={Users}
                    />

                    <SelectField
                        label="Region"
                        name="region"
                        value={campaign.region || 'All'}
                        onChange={handleInputChange}
                        options={["All", "United States", "United Kingdom", "Middle East"]}
                        icon={Globe}
                    />

                     <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">Start Date</label>
                            <input type="date" name="startDate" value={formatDateForInput(campaign.startDate)} onChange={handleInputChange} className="w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">Duration (weeks)</label>
                            <input type="number" name="duration" value={campaign.duration || 4} onChange={handleInputChange} min="1" max="12" className="w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white" />
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-2">
                     <div className="bg-slate-800/50 rounded-lg border border-slate-700 h-full flex flex-col p-1">
                        <div className="p-4 border-b border-slate-700/50"><label className="block text-sm font-medium text-slate-300">Campaign Goal / Description</label></div>
                        <textarea name="description" value={campaign.description} onChange={handleInputChange} className="w-full flex-grow p-4 bg-transparent focus:outline-none text-slate-200 resize-none" />
                        <div className="p-4 border-t border-slate-700/50 mt-auto">
                            <Button onClick={handleSuggestion} disabled={!brandHub || isSuggesting} icon={Sparkles} className={`w-full ${isSuggesting ? 'is-generating' : ''}`}>
                                {isSuggesting ? 'Brainstorming...' : 'Start with AI suggestion'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
const Step2Insights = ({ campaign, brandHub, insights, setInsights }) => { 
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchInsights = async () => {
            if (insights) {
                setIsLoading(false);
                return;
            }
            if (!campaign.product || campaign.product.length === 0 || !brandHub) {
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError(null);

            const selectedProducts = brandHub.products.filter(p => campaign.product.includes(p.title));

            if (selectedProducts.length === 0) {
                setError("Could not find the selected product(s) in your Brand Compass.");
                setIsLoading(false);
                return;
            }
            
            try {
                // --- THIS IS THE FIX ---
                // The payload now correctly sends the 'products' array alongside the 'campaign' object,
                // which is what the updated backend route expects.
                const data = await apiService.generateCampaignInsights({
                    campaign,
                    products: selectedProducts 
                });
                setInsights(data);
            } catch (err) {
                setError(err.response?.data?.message || "An unknown error occurred while generating insights.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchInsights();
    }, [campaign, brandHub, insights, setInsights]); // Dependency array updated to watch the whole campaign object

    const getCategoryIcon = (category = '') => {
        const cat = category.toLowerCase();
        if (cat.includes('datasheet')) return <FileText className="w-5 h-5 text-sky-400" />;
        if (cat.includes('whitepaper')) return <Rss className="w-5 h-5 text-orange-400" />;
        if (cat.includes('case study')) return <CheckCircle className="w-5 h-5 text-emerald-400" />;
        if (cat.includes('webinar')) return <Video className="w-5 h-5 text-violet-400" />;
        return <Info className="w-5 h-5 text-slate-400" />;
    };

    if (isLoading) return <LoadingSpinner text="Analyzing content & generating recommendations..." />;
    if (error) return <ErrorDisplay message={error} />;
    
    if (!insights) return (
        <div className="text-center p-8">
            <p className="text-slate-400">Insights will be generated here once product(s) and an ICP are defined in Step 1.</p>
        </div>
    );

    const { ctaRecommendations, competitorStatCard, campaignAdditions } = insights;

    return (
        <div>
            <SectionTitle icon={<BrainCircuit className="w-6 h-6 text-sky-400" />} title="Key Insights & Recommendations" />
            
            {ctaRecommendations && ctaRecommendations.length > 0 && (
                <div className="mb-8">
                    <h3 className="font-semibold text-lg mb-3 text-slate-200">Top CTA Recommendations</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {ctaRecommendations.map((cta, i) => (
                            <div key={i} className="bg-slate-800/50 rounded-lg shadow-lg p-4 border border-slate-700">
                                <div className="flex items-center mb-2">
                                    {getCategoryIcon(cta.category)}
                                    <a href={cta.url} target="_blank" rel="noopener noreferrer" className="ml-3 font-bold text-slate-100 hover:text-purple-400 transition-colors">
                                        {cta.title}
                                    </a>
                                </div>
                                <div className="text-xs text-slate-400 mb-3 ml-8">{cta.category}</div>
                                <div className="bg-slate-900/70 border-l-4 border-purple-500 p-3 rounded-r-md">
                                    <p className="text-sm text-slate-300">
                                        <b className="text-purple-400">Justification:</b> {cta.justification}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {competitorStatCard && competitorStatCard.length > 0 && (
                <div className="mb-8">
                    <h3 className="font-semibold text-lg mb-3 text-slate-200">Competitor Analysis Stat Card</h3>
                    <Card>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="border-b border-slate-700">
                                    <tr>
                                        <th className="p-2">Competitor</th>
                                        <th className="p-2">Key Features</th>
                                        <th className="p-2">Target Audience</th>
                                        <th className="p-2">Marketing Messaging</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {competitorStatCard.map((comp, i) => (
                                        <tr key={i} className="border-b border-slate-800 align-top">
                                            <td className="p-2 font-semibold max-w-xs truncate" title={comp.competitor}>{comp.competitor}</td>
                                            <td className="p-2">{comp.keyFeatures}</td>
                                            <td className="p-2">{comp.targetAudience}</td>
                                            <td className="p-2">{comp.marketingMessaging}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </div>
            )}

            {campaignAdditions && campaignAdditions.length > 0 && (
                <div>
                     <h3 className="font-semibold text-lg mb-3 text-slate-200">3 Highly Relevant Campaign Additions</h3>
                     <div className="space-y-4">
                        {campaignAdditions.map((rec, i) => (
                             <Card key={i} className="!bg-slate-800/50 border border-slate-700 p-4">
                                <h4 className="font-bold text-md text-purple-400 mb-2">{i+1}. {rec.type}</h4>
                                <p className="mb-2 text-slate-300"><strong>Recommendation:</strong> {rec.recommendation}</p>
                                <p className="text-slate-400"><strong>Focus:</strong> {rec.focus}</p>
                             </Card>
                        ))}
                     </div>
                </div>
            )}
        </div>
    );
};

// --- Other Step Components (Step3Targeting, Step4Channels, etc.) ---


const BuyingGroupInput = ({ label, description, value, onChange, icon: Icon, color }) => (
    <div className={`bg-slate-800/70 rounded-xl p-4 border border-slate-700 flex flex-col`}>
        <div className="flex items-center gap-2 mb-2">
            <Icon className={`w-5 h-5 ${color}`} />
            <label className="block text-md font-semibold text-slate-200">{label}</label>
        </div>
        <p className="text-xs text-slate-500 mb-3 flex-grow">{description}</p>
        <textarea
            value={value}
            onChange={onChange}
            className="w-full h-24 p-2 bg-slate-900/70 border border-slate-700 rounded-md text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-400"
            placeholder="Enter comma-separated job titles..."
        />
    </div>
);

// STYLING: A new helper component to visualize ratings.
const RatingVisualizer = ({ rating }) => {
    const level = { 'High': 3, 'Medium': 2, 'Low': 1, 'None': 0 }[rating] || 0;
    const colors = { 3: 'bg-green-500', 2: 'bg-yellow-500', 1: 'bg-red-500', 0: 'bg-slate-600' };
    return (
        <div className="flex gap-1.5 items-center">
            {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className={`w-3 h-3 rounded-full ${i < level ? colors[level] : 'bg-slate-600'}`}></div>
            ))}
            <span className="ml-1 text-sm font-semibold">{rating}</span>
        </div>
    );
};

const Step3Targeting = ({ campaign, setCampaign, brandHub }) => {
    const [isGeneratingTitles, setIsGeneratingTitles] = useState(false);
    const [error, setError] = useState(null);

    const handleGenerateTitles = async () => {
        setIsGeneratingTitles(true);
        setError(null);
        try {
            const products = brandHub.products.filter(p => campaign.product.includes(p.title));
            const icp = brandHub.customerProfiles.find(p => p.name === campaign.icp);
            if (products.length === 0 || !icp) throw new Error("Product and ICP must be selected.");
            
            const result = await apiService.generateBuyingCommittee({ products, icp });
            
            setCampaign(prev => ({
                ...prev,
                buyingCommittee: {
                    decisionMakers: (result.titles.decisionMakers || []).join(', '),
                    influencers: (result.titles.influencers || []).join(', '),
                    users: (result.titles.users || []).join(', ')
                }
            }));
        } catch (err) { setError(err.message || 'Failed to generate titles.');
        } finally { setIsGeneratingTitles(false); }
    };

    const handleBuyingGroupChange = (e, groupName) => {
        setCampaign(prev => ({ ...prev, buyingCommittee: { ...prev.buyingCommittee, [groupName]: e.target.value }}));
    };

    return (
        <div>
            <SectionTitle icon={<Target className="w-6 h-6 text-emerald-400" />} title="Define Buying Committee" />
            <p className="mb-6 text-slate-400">Generate a list of job titles for your buying committee with AI.</p>
            
            <div className="text-center mb-6">
                <Button onClick={handleGenerateTitles} disabled={isGeneratingTitles} icon={Sparkles} className={`${isGeneratingTitles ? 'animate-pulse' : ''}`}>
                    {isGeneratingTitles ? 'Generating Titles...' : 'Generate Buying Committee with AI'}
                </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <BuyingGroupInput 
                    label="Decision Makers" description="e.g., CHRO, VP of People"
                    icon={Users} color="text-red-400" value={campaign.buyingCommittee?.decisionMakers || ''} 
                    onChange={e => handleBuyingGroupChange(e, 'decisionMakers')} 
                />
                <BuyingGroupInput 
                    label="Influencers" description="e.g., HRIS Manager, People Analytics Lead"
                    icon={Users} color="text-yellow-400" value={campaign.buyingCommittee?.influencers || ''} 
                    onChange={e => handleBuyingGroupChange(e, 'influencers')}
                />
                <BuyingGroupInput 
                    label="Users" description="e.g., HR Business Partner, Recruiter"
                    icon={Users} color="text-green-400" value={campaign.buyingCommittee?.users || ''} 
                    onChange={e => handleBuyingGroupChange(e, 'users')}
                />
            </div>
            {error && <ErrorDisplay message={error} />}
        </div>
    );
};

const Step4Channels = ({ campaign, setCampaign }) => {
    
    // Now reads directly from the central channelConfig
    const channelOptions = Object.entries(channelConfig)
        .filter(([id]) => id !== 'default')
        .map(([id, { name, icon }]) => ({ id, name, icon }));

    const toggleChannel = (channel) => {
        setCampaign(prev => {
            const isSelected = prev.selectedChannels.find(c => c.id === channel.id);
            // --- FIX: Only store the ID and name, NOT the icon component ---
            const newChannels = isSelected 
                ? prev.selectedChannels.filter(c => c.id !== channel.id) 
                : [...prev.selectedChannels, { id: channel.id, name: channel.name }];
            return { ...prev, selectedChannels: newChannels };
        });
    };
    
    const getChannelBadge = (channelId) => { /* ... no change ... */ };

    return (
        <div>
            <SectionTitle icon={<Megaphone className="w-6 h-6 text-violet-400" />} title="Select Channel" />
            <p className="mb-6 text-slate-400">Select the channels you want to include in this campaign.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {channelOptions.map(channel => {
                    const isSelected = campaign.selectedChannels.find(c => c.id === channel.id);
                    return (
                        <div 
                            key={channel.id} 
                            onClick={() => toggleChannel(channel)}
                            className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 flex flex-col items-center justify-center h-32 group ${isSelected ? 'border-violet-500 bg-violet-900/30 shadow-lg shadow-violet-500/20 scale-105' : 'border-slate-700 bg-slate-800 hover:border-slate-600'}`}>
                            {getChannelBadge(channel.id)}
                            {isSelected && (
                                <div className="absolute top-2 left-2 bg-violet-500 rounded-full p-1">
                                    <CheckCircle size={16} className="text-white" />
                                </div>
                            )}
                            <div className={`mb-3 h-8 w-8 transition-colors ${isSelected ? 'text-violet-400' : 'text-slate-400'}`}>
                                {React.cloneElement(channel.icon, { size: 32 })}
                            </div>
                            <p className={`font-semibold text-center text-sm transition-colors ${isSelected ? 'text-violet-300' : 'text-slate-300'}`}>
                                {channel.name}
                            </p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
const ContentEditor = ({ item, onUpdate, onGenerateAI }) => {
    const inputClasses = "bg-slate-700 border border-slate-600 text-white text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5";

    const handleInputChange = (e) => {
        onUpdate({ ...item.details, [e.target.name]: e.target.value });
    };

    switch (item.channelId) {
        case 'email':
            return (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1">Sender Name</label>
                            <input type="text" name="senderName" value={item.details.senderName || ''} onChange={handleInputChange} className={inputClasses} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1">Sender Email</label>
                            <input type="email" name="senderEmail" value={item.details.senderEmail || ''} onChange={handleInputChange} className={inputClasses} />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Subject Line</label>
                        <div className="flex">
                            <input type="text" name="subject" value={item.details.subject || ''} onChange={handleInputChange} className={`${inputClasses} rounded-r-none`} />
                            <button onClick={() => onGenerateAI(item.id)} className="btn-primary rounded-l-none flex items-center px-3"><Sparkles size={16} className="mr-1" /> AI</button>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Email Body</label>
                        <textarea name="body" value={item.details.body || ''} onChange={handleInputChange} rows="8" className={inputClasses}></textarea>
                    </div>
                </div>
            );
        case 'linkedin_ads':
        case 'facebook_ads':
        case 'instagram_ads':
            return (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Headline</label>
                        <input type="text" name="headline" value={item.details.headline || ''} onChange={handleInputChange} className={inputClasses} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Primary Text</label>
                        <textarea name="primaryText" value={item.details.primaryText || ''} onChange={handleInputChange} rows="5" className={inputClasses}></textarea>
                    </div>
                </div>
            );
        case 'google_adwords':
             return (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Headline 1</label>
                        <input type="text" name="headline1" value={item.details.headline1 || ''} onChange={handleInputChange} className={inputClasses} maxLength="30" />
                    </div>
                     <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Headline 2</label>
                        <input type="text" name="headline2" value={item.details.headline2 || ''} onChange={handleInputChange} className={inputClasses} maxLength="30" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Description</label>
                        <textarea name="description" value={item.details.description || ''} onChange={handleInputChange} rows="3" className={inputClasses} maxLength="90"></textarea>
                    </div>
                </div>
            );
        default:
            return (
                <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Content</label>
                    <textarea name="content" value={item.details.content || ''} onChange={handleInputChange} rows="8" className={inputClasses}></textarea>
                </div>
            );
    }
};


// --- UPDATED: TimelineItem ---
const TimelineItem = ({ item, onEdit, onDelete, onSchedule }) => {
    // --- FIX: Look up metadata from the central channelConfig ---
    const meta = channelConfig[item.channelId] || channelConfig.default;
    const isOperational = item.taskType === 'Operational';

    return (
        <div className={`group relative bg-slate-900/70 rounded-lg p-3 flex flex-col border border-slate-700/50 border-t-4 transition-all duration-300 hover:border-slate-600 ${isOperational ? 'border-t-slate-500' : meta.borderColor}`}>
            <div className="flex-grow">
                <div className={`flex items-start gap-3`}>
                    <div className={`mt-1 ${isOperational ? 'text-slate-400' : meta.textColor}`}>
                        {isOperational ? <Settings size={18}/> : React.cloneElement(meta.icon, { size: 18 })}
                    </div>
                    <div className="flex-1">
                        <p className="font-bold text-white text-sm leading-tight break-words">{item.action}</p>
                        <p className="text-xs text-slate-400 mt-1">{item.channel}</p>
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-end gap-1 mt-3 pt-2 border-t border-slate-800/50">
                <button title="Schedule" onClick={() => onSchedule(item)} className="p-1.5 text-slate-500 hover:text-white rounded-md hover:bg-slate-700 transition-colors"><Calendar size={14} /></button>
                <button title="Edit" onClick={() => onEdit(item)} className="p-1.5 text-slate-500 hover:text-white rounded-md hover:bg-slate-700 transition-colors"><Edit size={14} /></button>
                <button title="Delete" onClick={() => onDelete(item.id)} className="p-1.5 text-slate-500 hover:text-red-400 rounded-md hover:bg-slate-700 transition-colors"><Trash2 size={14} /></button>
            </div>
        </div>
    );
};

const ContentEditorSidebar = ({ item, onClose, onUpdate, brandHub }) => {
    const [view, setView] = useState('create');
    const [cargoBayAssets, setCargoBayAssets] = useState([]);
    const [isLoadingAssets, setIsLoadingAssets] = useState(false);
    
    // The details of the item being edited are now the 'formData'
    const [formData, setFormData] = useState(item.details || {});

    useEffect(() => {
        if (view === 'select') {
            setIsLoadingAssets(true);
            apiService.fetchPublishedAssets()
                .then(setCargoBayAssets)
                .finally(() => setIsLoadingAssets(false));
        }
    }, [view]);

    const handleSaveAndClose = () => {
        // Find the title from the form data, or use the original action
        const newTitle = formData.title || item.action;
        onUpdate({ ...item, action: newTitle, details: formData, isComplete: true });
        onClose();
    };

    const handleSelectFromCargoBay = (asset) => {
        // When selecting from Cargo Bay, we need to parse the content string
        let parsedContent = {};
        try {
            parsedContent = JSON.parse(asset.content);
        } catch (e) {
            // Fallback for older assets that stored content as a plain string
            parsedContent = { content: asset.content };
        }

        const newDetails = { title: asset.title, ...parsedContent };
        setFormData(newDetails);
        onUpdate({ ...item, action: asset.title, details: newDetails, linkedAssetId: asset._id, isComplete: true });
        setView('create');
    };

    // The file upload handler can be passed down if needed, or defined here
    const handleFileUpload = async (file) => {
        const response = await apiService.uploadContentAsset(file);
        return response.url;
    };

    return (
        <aside className="fixed top-0 right-0 h-full w-96 bg-slate-900 p-4 border-l border-slate-700 flex flex-col z-30 animate-slide-in">
            <div className="flex justify-between items-center mb-4 pb-2 border-b border-slate-700 flex-shrink-0">
                <div className="flex gap-2">
                    <button onClick={() => setView('create')} className={`px-3 py-1 text-sm ${view === 'create' ? 'font-bold text-white' : 'text-slate-400'}`}>Create New</button>
                    <button onClick={() => setView('select')} className={`px-3 py-1 text-sm ${view === 'select' ? 'font-bold text-white' : 'text-slate-400'}`}>Select From Cargo Bay</button>
                </div>
                <button onClick={onClose} className="p-1 text-slate-500 hover:text-white text-2xl">&times;</button>
            </div>
            
            <div className="flex-grow overflow-y-auto pr-2">
                {view === 'create' ? (
                    <DynamicCreatorForm 
                        channel={item.channelId}
                        formData={formData}
                        setFormData={setFormData}
                        handleFileUpload={handleFileUpload}
                    />
                ) : (
                    <div>
                        <h3 className="font-bold text-lg text-white mb-4">Select from Cargo Bay</h3>
                        <div className="space-y-2">
                            {isLoadingAssets ? <p>Loading assets...</p> : cargoBayAssets.map(asset => (
                                <div key={asset._id} className="p-2 bg-slate-800 rounded-md hover:bg-slate-700 cursor-pointer" onClick={() => handleSelectFromCargoBay(asset)}>
                                    <p className="font-semibold text-white">{asset.title}</p>
                                    <p className="text-xs text-slate-400">{asset.contentType}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            
            <div className="mt-4 flex justify-end gap-2 border-t border-slate-700 pt-4 flex-shrink-0">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button variant="success" onClick={handleSaveAndClose}>Save and Close</Button>
            </div>
        </aside>
    );
};


// Replace your entire 'const Step5ContentAndTimeline' component with this one

const Step5TimelinePlanning = ({ campaign, setCampaign, brandHub, setToastMessage, setConfirmation }) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [schedulingItem, setSchedulingItem] = useState(null);
    const sliderRef = useRef(null);
    const [scrollState, setScrollState] = useState({ width: 0, scrollWidth: 0, scrollLeft: 0 });

    useEffect(() => {
        const slider = sliderRef.current;
        if (!slider) return;
        const updateScrollState = () => {
            setScrollState({
                width: slider.clientWidth,
                scrollWidth: slider.scrollWidth,
                scrollLeft: slider.scrollLeft,
            });
        };
        slider.addEventListener('scroll', updateScrollState);
        const resizeObserver = new ResizeObserver(updateScrollState);
        resizeObserver.observe(slider);
        updateScrollState();
        return () => {
            slider.removeEventListener('scroll', updateScrollState);
            resizeObserver.unobserve(slider);
        };
    }, [campaign.timeline]);

    const handleScrollbarChange = (newScrollLeft) => {
        if (sliderRef.current) sliderRef.current.scrollLeft = newScrollLeft;
    };

    const handleGenerateTimelinePlan = async () => {
        setIsGenerating(true);
        try {
            const products = brandHub.products.filter(p => campaign.product.includes(p.title));
            const icp = brandHub.customerProfiles.find(p => p.name === campaign.icp);
            
            const response = await apiService.generateAITimeline({ campaign, products, icp, insights: campaign.insights });

            // --- THIS IS THE FIX ---
            // The API returns an object like { timeline: [...] }. We must extract the array.
            if (response && Array.isArray(response.timeline)) {
                setCampaign(prev => ({ ...prev, timeline: response.timeline }));
            } else {
                console.error("API did not return a valid timeline array:", response);
                setCampaign(prev => ({ ...prev, timeline: [] })); // Set empty array to prevent crash
                setToastMessage('Error: Received an invalid format from the timeline generator.');
            }

        } catch (error) {
            setToastMessage(`Error: ${error.message || 'Failed to generate timeline plan.'}`);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleUpdateItem = (updatedItem) => {
        setCampaign(prev => ({
            ...prev,
            timeline: prev.timeline.map(item => item.id === updatedItem.id ? updatedItem : item)
        }));
    };

    const handleDeleteItem = (itemId) => {
        setConfirmation({
            isOpen: true,
            title: 'Delete Item',
            message: 'Are you sure you want to delete this timeline item?',
            onConfirm: () => {
                setCampaign(prev => ({...prev, timeline: prev.timeline.filter(item => item.id !== itemId)}));
                setConfirmation({ isOpen: false });
            }
        });
    };
    
    const handleScheduleSave = (date) => {
        handleUpdateItem({ ...schedulingItem, scheduledDate: date });
        setSchedulingItem(null);
    };

    const timelineByWeek = useMemo(() => {
        const weeks = Array.from({ length: campaign.duration || 1 }, (_, i) => i + 1);
        const timelineArray = Array.isArray(campaign.timeline) ? campaign.timeline : [];
        const grouped = timelineArray.reduce((acc, item) => {
            const week = item.week;
            if (!acc[week]) acc[week] = [];
            acc[week].push(item);
            return acc;
        }, {});
        weeks.forEach(weekNum => { if (!grouped[weekNum]) grouped[weekNum] = []; });
        return grouped;
    }, [campaign.timeline, campaign.duration]);

    return (
        <div>
            {schedulingItem && <EditDateModal isOpen={true} onClose={() => setSchedulingItem(null)} onSave={handleScheduleSave} initialDate={schedulingItem.scheduledDate} />}
            
            <SectionTitle icon={<Calendar className="w-6 h-6 text-rose-400" />} title="Timeline Planning">
                <CustomTimelineScrollbar {...scrollState} onScroll={handleScrollbarChange} />
            </SectionTitle>
             <p className="mb-6 text-slate-400">Generate a strategic timeline of actions for your campaign. You will generate the actual content in the next step.</p>
            
            {(campaign.timeline || []).length === 0 && (
                <div className="text-center p-8 bg-slate-800/50 rounded-xl">
                    <Button onClick={handleGenerateTimelinePlan} disabled={isGenerating} icon={Sparkles} variant="primary" className={`${isGenerating ? 'is-generating' : ''}`}>
                        {isGenerating ? 'Generating Plan...' : 'Generate Timeline Plan with AI'}
                    </Button>
                </div>
            )}
            
            <div className="grid grid-cols-1">
                <div ref={sliderRef} className="overflow-x-auto pb-4" style={{ scrollbarWidth: 'none' }}>
                    <div className="flex gap-4" style={{ minWidth: `${(campaign.duration || 1) * 21}rem` }}>
                        {Object.entries(timelineByWeek).map(([weekNum, items]) => (
                            <div key={weekNum} className="w-80 flex-shrink-0 bg-slate-800/50 rounded-xl flex flex-col">
                                <h3 className="font-bold text-slate-200 p-4 border-b border-slate-700/50">Week {weekNum}</h3>
                                <div className="p-2 space-y-2 flex-grow overflow-y-auto">
                                    {items.map((item) => (
                                        <TimelineItem 
                                            key={item.id} 
                                            item={item} 
                                            onDelete={handleDeleteItem} 
                                            onSchedule={setSchedulingItem} 
                                            onEdit={() => {}}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
// --- NEW: Step6ContentGeneration ---
// --- NEW: Step6ContentGeneration with Selection Controls ---
const Step6ContentGeneration = ({ campaign, setCampaign, brandHub, setToastMessage }) => {
    const [generationStatus, setGenerationStatus] = useState({});
    const [isGenerating, setIsGenerating] = useState(false);
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [editingItem, setEditingItem] = useState(null);
    const pollingIntervalRef = useRef(null);
    const [isSelectionMenuOpen, setIsSelectionMenuOpen] = useState(false);
    const selectionMenuRef = useRef(null);

    const contentItems = useMemo(() => campaign.timeline.filter(item => item.taskType === 'Content'), [campaign.timeline]);

    // --- NEW: Get unique channels for the selection menu ---
    const availableChannels = useMemo(() => {
        const channels = new Map();
        contentItems.forEach(item => {
            if (!channels.has(item.channelId)) {
                const meta = channelConfig[item.channelId] || channelConfig.default;
                channels.set(item.channelId, meta.name);
            }
        });
        return Array.from(channels.entries()).map(([id, name]) => ({ id, name }));
    }, [contentItems]);

    // --- NEW: Handlers for bulk selection ---
    const handleSelectAll = () => {
        setSelectedItems(new Set(contentItems.map(item => item.id)));
        setIsSelectionMenuOpen(false);
    };

    const handleDeselectAll = () => {
        setSelectedItems(new Set());
        setIsSelectionMenuOpen(false);
    };

    const handleSelectByChannel = (channelId) => {
        const itemsToSelect = contentItems
            .filter(item => item.channelId === channelId)
            .map(item => item.id);
        setSelectedItems(new Set(itemsToSelect));
        setIsSelectionMenuOpen(false);
    };
    
    // --- NEW: Effect to close dropdown on outside click ---
    useEffect(() => {
        function handleClickOutside(event) {
            if (selectionMenuRef.current && !selectionMenuRef.current.contains(event.target)) {
                setIsSelectionMenuOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [selectionMenuRef]);

    const pollForUpdates = useCallback(async () => {
        try {
            const updatedCampaign = await apiService.fetchCampaign(campaign._id);
            setCampaign(updatedCampaign); // Update the entire campaign state

            const newStatus = { ...generationStatus };
            let stillGenerating = false;
            
            updatedCampaign.timeline.forEach(item => {
                if (generationStatus[item.id] === 'generating') {
                    if (item.isComplete) {
                        let hasError = (typeof item.details === 'object' && item.details?.error) ||
                                       (typeof item.details === 'string' && item.details.toLowerCase().includes('error'));
                        newStatus[item.id] = hasError ? 'error' : 'done';
                    } else {
                        stillGenerating = true; // At least one item is still in progress
                    }
                }
            });

            setGenerationStatus(newStatus);

            if (!stillGenerating) {
                setIsGenerating(false);
                setToastMessage('Content generation complete!');
            }
        } catch (error) {
            console.error("Polling failed:", error);
            setIsGenerating(false); // Stop on error
        }
    }, [campaign._id, setCampaign, generationStatus, setToastMessage]);

    useEffect(() => {
        if (isGenerating) {
            pollForUpdates();
            pollingIntervalRef.current = setInterval(pollForUpdates, 5000);
        } else {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        }
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, [isGenerating, pollForUpdates]);

    const handleViewHtmlReport = (htmlContent) => {
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 100);
    };

    const toggleSelection = (itemId) => {
        setSelectedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    };

    const handleGenerateContent = async () => {
        if (selectedItems.size === 0) return;

        setIsGenerating(true);
        const itemsToGenerate = contentItems.filter(item => selectedItems.has(item.id));
        
        const newStatus = { ...generationStatus };
        itemsToGenerate.forEach(item => newStatus[item.id] = 'generating');
        setGenerationStatus(newStatus);
        setSelectedItems(new Set());

        try {
            const product = brandHub.products.find(p => p.title === campaign.product);
            const icp = brandHub.customerProfiles.find(p => p.name === campaign.icp);

            await apiService.generateAllCampaignContent({
                campaign,
                product,
                icp,
                insights: campaign.insights,
                itemsToGenerate
            });

            setToastMessage('Content generation process has started...');

        } catch (error) {
            console.error("Failed to trigger content generation workflow", error);
            setToastMessage(`Error: ${error.message || 'Could not start generation.'}`);
            const errorStatus = { ...generationStatus };
            itemsToGenerate.forEach(item => errorStatus[item.id] = 'error');
            setGenerationStatus(errorStatus);
            setIsGenerating(false);
        }
    };

    const getStatusIndicator = (itemId) => {
        const status = generationStatus[itemId];
        if (status === 'generating') return <Loader2 className="w-5 h-5 text-yellow-400 animate-spin" />;
        if (status === 'done') return <CheckCircle className="w-5 h-5 text-green-400" />;
        if (status === 'error') return <AlertTriangle className="w-5 h-5 text-red-400" />;
        return <div className="w-5 h-5"></div>;
    };
    
    return (
        <div>
            {editingItem && <EditContentModal item={editingItem} onClose={() => setEditingItem(null)} onSave={(updatedItem) => {
                setCampaign(prev => ({...prev, timeline: prev.timeline.map(item => item.id === updatedItem.id ? updatedItem : item)}));
            }} />}
            <SectionTitle icon={<PenSquare className="w-6 h-6 text-cyan-400" />} title="AI Content Generation">
                <div className="flex items-center gap-2">
                    <div className="relative" ref={selectionMenuRef}>
                        <Button
                            variant="secondary"
                            onClick={() => setIsSelectionMenuOpen(prev => !prev)}
                            className="w-28" // Give it a fixed width
                        >
                           <span className="flex-grow text-left">Select</span>
                           <ChevronDown size={16} className={`transition-transform ${isSelectionMenuOpen ? 'rotate-180' : ''}`} />
                        </Button>
                        {isSelectionMenuOpen && (
                            <div className="absolute right-0 top-full mt-2 w-56 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-10 animate-fade-in-down">
                                <ul className="p-1 text-slate-200">
                                    <li><button onClick={handleSelectAll} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-700 rounded-md transition-colors">Select All</button></li>
                                    <li><button onClick={handleDeselectAll} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-700 rounded-md transition-colors">Deselect All</button></li>
                                    {availableChannels.length > 0 && <li className="my-1 border-t border-slate-700"></li>}
                                    {availableChannels.map(channel => (
                                        <li key={channel.id}>
                                            <button onClick={() => handleSelectByChannel(channel.id)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-700 rounded-md transition-colors">
                                                Select All {channel.name}s
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                    <Button onClick={handleGenerateContent} disabled={isGenerating || selectedItems.size === 0} icon={Sparkles} variant="primary">
                        {isGenerating ? 'Generating...' : `Generate for ${selectedItems.size} Selected`}
                    </Button>
                </div>
            </SectionTitle>
            <p className="mb-6 text-slate-400">Select one or more content actions below, then click the button to generate the copy with AI. You can monitor the progress here.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {contentItems.map(item => {
                    const meta = channelConfig[item.channelId] || channelConfig.default;
                    const isSelected = selectedItems.has(item.id);
                    
                    const isAdChannel = item.channelId === 'google-ad' || item.channelId === 'linkedin-ad';
                    const isDone = item.isComplete;
                    
                    return (
                        <div key={item.id} className={`relative bg-slate-800/60 rounded-lg p-4 border transition-all duration-200 ${isSelected ? 'border-brand-500 ring-2 ring-brand-500/50' : 'border-slate-700'}`}>
                            <div className="flex justify-between items-start">
                                <div className="flex items-center gap-3">
                                    <div className={`flex-shrink-0 p-2 rounded-full bg-slate-700/50 ${meta.textColor}`}>
                                        {React.cloneElement(meta.icon, { size: 20 })}
                                    </div>
                                    <div>
                                        <p className="font-bold text-slate-100">{item.action}</p>
                                        <p className="text-sm text-slate-400">{meta.name} - Week {item.week}</p>
                                    </div>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleSelection(item.id)}
                                    className="h-5 w-5 rounded bg-slate-700 border-slate-600 text-brand-500 focus:ring-brand-500"
                                />
                            </div>
                            <div className="mt-4 pt-4 border-t border-slate-700/50 flex justify-between items-center h-8">
                                <div className="flex items-center gap-2 text-sm">
                                    {getStatusIndicator(item.id)}
                                    <span className="text-slate-500 capitalize">
                                        {generationStatus[item.id] || (isDone ? 'Generated' : 'Pending')}
                                    </span>
                                </div>
                                {isDone && (
                                     <Button 
                                        variant="secondary" 
                                        onClick={() => isAdChannel ? handleViewHtmlReport(item.details) : setEditingItem(item)} 
                                        icon={Edit}
                                    >
                                        View/Edit
                                    </Button>
                                )}
                            </div>
                        </div>
                    );
                })}
                 {contentItems.length === 0 && (
                    <div className="text-center p-8 text-slate-500 md:col-span-2 lg:col-span-3">No content items in the timeline to generate.</div>
                )}
            </div>
        </div>
    );
};
// --- NEW: EditContentModal for Step 6 ---
const EditContentModal = ({ item, onClose, onSave }) => {
    const [editedDetails, setEditedDetails] = useState(item.details);

    if (!item) return null;

    const handleDetailChange = (e) => {
        setEditedDetails(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSave = () => {
        onSave({ ...item, details: editedDetails });
        onClose();
    };
    
    const meta = channelConfig[item.channelId] || channelConfig.default;

    return (
        <Modal isOpen={true} onClose={onClose} title={`Edit Content: ${item.action}`}>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                {Object.entries(editedDetails).map(([key, value]) => {
                    const isLongText = key === 'body' || key === 'content' || key === 'primaryText' || key.toLowerCase().includes('description');
                    return (
                        <div key={key}>
                            <label className="block text-sm font-medium text-slate-300 mb-1 capitalize">{key.replace(/([A-Z])/g, ' $1')}</label>
                            {isLongText ? (
                                <textarea 
                                    name={key}
                                    value={value}
                                    onChange={handleDetailChange}
                                    rows="8"
                                    className="w-full bg-slate-700 p-2 rounded-md text-sm"
                                />
                            ) : (
                                <input
                                    type="text"
                                    name={key}
                                    value={value}
                                    onChange={handleDetailChange}
                                    className="w-full bg-slate-700 p-2 rounded-md text-sm"
                                />
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="mt-6 flex justify-end gap-2">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button variant="primary" onClick={handleSave}>Save Changes</Button>
            </div>
        </Modal>
    );
};

// --- UPDATED: Step7Review with Download Functionality ---
const Step7Review = ({ campaign }) => {
    
    const handleDownloadReport = () => {
        const htmlContent = generateCampaignReportHTML(campaign, channelConfig);
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `campaign-report-${campaign.name.replace(/\s+/g, '-')}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // --- Helper function to get icons for CTA cards ---
    const getCategoryIcon = (category = '') => {
        const cat = category.toLowerCase();
        if (cat.includes('datasheet')) return <FileText className="w-5 h-5 text-sky-400" />;
        if (cat.includes('whitepaper')) return <Rss className="w-5 h-5 text-orange-400" />;
        if (cat.includes('case study')) return <CheckCircle className="w-5 h-5 text-emerald-400" />;
        if (cat.includes('webinar')) return <Video className="w-5 h-5 text-violet-400" />;
        return <Info className="w-5 h-5 text-slate-400" />;
    };

    const campaignEndDate = useMemo(() => {
        const scheduledItems = (campaign.timeline || []).filter(item => item.scheduledDate);
        if (scheduledItems.length === 0) {
            return 'TBD';
        }

        const latestDate = new Date(
            Math.max(...scheduledItems.map(item => new Date(item.scheduledDate)))
        );
        
        latestDate.setDate(latestDate.getDate() + 1);

        return latestDate.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    }, [campaign.timeline]);

    const formatDate = (date) => {
        if (!date) return 'Unscheduled';
        return new Date(date).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const sortedTimeline = useMemo(() => {
        return [...(campaign.timeline || [])].sort((a, b) => {
            const dateA = a.scheduledDate ? new Date(a.scheduledDate).getTime() : 0;
            const dateB = b.scheduledDate ? new Date(b.scheduledDate).getTime() : 0;
            if (dateA === 0 && dateB === 0) return 0;
            if (dateA === 0) return 1;
            if (dateB === 0) return -1;
            return dateA - dateB;
        });
    }, [campaign.timeline]);

    return (
        <div>
            <SectionTitle icon={<CheckCircle className="w-6 h-6 text-emerald-400" />} title="Review & Launch">
                <Button onClick={handleDownloadReport} variant="secondary" icon={Download}>
                    Download Report
                </Button>
            </SectionTitle>
            
            <h2 className="text-2xl font-bold text-slate-100 -mt-2 mb-2">{campaign.name}</h2>
            <p className="mb-8 text-slate-400">This is a complete summary of your campaign. Once launched, this view will be used to track its progress.</p>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* --- COLUMN 1: STRATEGY & TARGETING --- */}
                <div className="space-y-6">
                    <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6">
                        <h3 className="flex items-center gap-3 text-xl font-bold text-white mb-4"><Lightbulb className="text-yellow-400" /> Campaign Goal</h3>
                        <p className="text-sm text-slate-400 mt-2">{campaign.description}</p>
                    </div>
                    <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6">
                        <h3 className="flex items-center gap-3 text-xl font-bold text-white mb-4"><Target className="text-emerald-400" /> Targeting</h3>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between items-center"><span className="text-slate-400">Product/Service:</span> <strong className="text-slate-100">{campaign.product}</strong></div>
                            <div className="flex justify-between items-center"><span className="text-slate-400">Ideal Customer Profile:</span> <strong className="text-slate-100">{campaign.icp}</strong></div>
                            <div className="border-t border-slate-700/50 my-3"></div>
                            {/* Target Audience Info is located here */}
                            <h4 className="font-semibold text-slate-300">Buying Committee:</h4>
                            <ul className="list-disc list-inside text-slate-400 pl-2 space-y-1">
                                <li><strong>Decision Makers:</strong> {campaign.buyingCommittee?.decisionMakers}</li>
                                <li><strong>Influencers:</strong> {campaign.buyingCommittee?.influencers}</li>
                                <li><strong>Users:</strong> {campaign.buyingCommittee?.users}</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* --- COLUMN 2: CHANNELS & METRICS --- */}
                <div className="space-y-6">
                    <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6">
                        <h3 className="flex items-center gap-3 text-xl font-bold text-white mb-4"><Megaphone className="text-violet-400" /> Channel Mix</h3>
                        <div className="flex flex-wrap gap-4">
                            {(campaign.selectedChannels || []).map(channel => {
                                const icon = channelConfig[channel.id]?.icon || <FileText />;
                                return (
                                    <div key={channel.id} className="flex items-center gap-2 bg-slate-800/70 p-2 rounded-lg">
                                        {React.cloneElement(icon, { size: 20, className: "text-slate-300" })}
                                        <span className="text-sm font-medium text-slate-200">{channel.name}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6">
                         <h3 className="flex items-center gap-3 text-xl font-bold text-white mb-4"><BarChart2 className="text-rose-400" /> Key Metrics</h3>
                         <div className="space-y-3 text-sm">
                            {/* Start and End dates are located here */}
                            <div className="flex justify-between items-center"><span className="text-slate-400">Start Date:</span> <strong className="text-slate-100">{formatDate(campaign.startDate)}</strong></div>
                            <div className="flex justify-between items-center"><span className="text-slate-400">Calculated End Date:</span> <strong className="text-slate-100">{campaignEndDate}</strong></div>
                            <div className="flex justify-between items-center"><span className="text-slate-400">Duration:</span> <strong className="text-slate-100">{campaign.duration} Weeks</strong></div>
                            <div className="border-t border-slate-700/50 my-3"></div>
                             <h4 className="font-semibold text-slate-300">Key Performance Indicators:</h4>
                             <ul className="list-disc list-inside text-slate-400 pl-2 space-y-1">
                                <li><strong>Primary:</strong> {campaign.kpis?.primary}</li>
                                <li><strong>Secondary:</strong> {campaign.kpis?.secondary}</li>
                            </ul>
                         </div>
                    </div>
                </div>

                {/* --- COLUMN 3: TIMELINE SUMMARY --- */}
                <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6">
                    <h3 className="flex items-center gap-3 text-xl font-bold text-white mb-4"><Calendar className="text-sky-400" /> Timeline Summary</h3>
                    <div className="space-y-3 max-h-[600px] overflow-y-auto custom-scrollbar pr-2">
                        {sortedTimeline.map(item => {
                            const icon = channelConfig[item.channelId]?.icon || <FileText />;
                            return (
                                <div key={item.id} className="flex items-start gap-3 p-2 rounded-md bg-slate-800/60">
                                    <div className="flex-shrink-0 mt-1">{React.cloneElement(icon, { size: 18, className: "text-slate-400" })}</div>
                                    <div className="flex-1">
                                        <p className="font-semibold text-sm text-slate-100">{item.action}</p>
                                        <p className="text-xs text-slate-400">Week {item.week} • {formatDate(item.scheduledDate)}</p>
                                    </div>
                                </div>
                            );
                        })}
                         {(campaign.timeline || []).length === 0 && <p className="text-sm text-slate-500 text-center py-8">No actions added to timeline.</p>}
                    </div>
                </div>
            </div>
            
            {/* --- NEW: Insights Section --- */}
            {campaign.insights && (
                <div className="mt-6 bg-slate-900/80 border border-slate-800 rounded-2xl p-6">
                    <h3 className="flex items-center gap-3 text-xl font-bold text-white mb-6"><BrainCircuit className="text-sky-400" /> AI-Generated Insights</h3>
                    <div className="space-y-6">
                        {/* CTA Recommendations */}
                        {campaign.insights.ctaRecommendations?.length > 0 && (
                             <div>
                                <h4 className="font-semibold text-lg mb-3 text-slate-200">Top CTA Recommendations</h4>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {campaign.insights.ctaRecommendations.map((cta, i) => (
                                        <div key={i} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                            <div className="flex items-center mb-2">
                                                {getCategoryIcon(cta.category)}
                                                <a href={cta.url} target="_blank" rel="noopener noreferrer" className="ml-3 font-bold text-slate-100 hover:text-purple-400 transition-colors">{cta.title}</a>
                                            </div>
                                            <p className="text-sm text-slate-300 border-l-4 border-purple-500 pl-3">
                                                <b className="font-semibold text-purple-400">Justification:</b> {cta.justification}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Campaign Additions */}
                        {campaign.insights.campaignAdditions?.length > 0 && (
                            <div>
                                <h4 className="font-semibold text-lg mb-3 text-slate-200">3 Highly Relevant Campaign Additions</h4>
                                <div className="space-y-4">
                                    {campaign.insights.campaignAdditions.map((rec, i) => (
                                        <div key={i} className="bg-slate-800/50 p-4 rounded-lg border-l-4 border-cyan-500">
                                            <h5 className="font-bold text-md text-cyan-400 mb-2">{i+1}. {rec.type}</h5>
                                            <p className="mb-2 text-slate-300"><strong>Recommendation:</strong> {rec.recommendation}</p>
                                            <p className="text-slate-400"><strong>Focus:</strong> {rec.focus}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Modal for the '+' button to add a new action
const AddItemModal = ({ isOpen, onClose, onAddItem, availableChannels, week, channelMeta }) => {
    const [selectedChannelId, setSelectedChannelId] = useState('');
    const [action, setAction] = useState('');

    const handleAdd = () => {
        onAddItem({ week, channelId: selectedChannelId, action });
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Add Action to Week ${week}`}>
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Select Channel</label>
                    <div className="grid grid-cols-3 gap-2">
                        {availableChannels.map(ch => {
                            const meta = channelMeta[ch.id] || channelMeta.default;
                            return (
                                <button key={ch.id} onClick={() => setSelectedChannelId(ch.id)} className={`p-2 rounded-md border-2 ${selectedChannelId === ch.id ? meta.color : 'border-slate-700 bg-slate-800'}`}>
                                    <div className="mx-auto w-6 h-6">{meta.icon}</div>
                                    <p className="text-xs mt-1">{ch.name}</p>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Action Title</label>
                    <input type="text" value={action} onChange={(e) => setAction(e.target.value)} className="w-full bg-slate-700 p-2 rounded-md" placeholder="e.g., Launch Welcome Email"/>
                </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button variant="primary" onClick={handleAdd} disabled={!selectedChannelId || !action}>Add Action</Button>
            </div>
        </Modal>
    );
};

// Modal for the calendar icon to set a date and time
const EditDateModal = ({ isOpen, onClose, onSave, initialDate }) => {
    const [date, setDate] = useState(initialDate ? new Date(initialDate).toISOString().split('T')[0] : '');
    const [time, setTime] = useState(initialDate ? new Date(initialDate).toTimeString().substring(0, 5) : '09:00');

    const handleSave = () => {
        if (date && time) {
            onSave(new Date(`${date}T${time}`));
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Schedule Action">
             <div className="flex gap-4">
                <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Date</label>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full bg-slate-700 p-2 rounded-md"/>
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Time</label>
                    <input type="time" value={time} onChange={e => setTime(e.target.value)} className="w-full bg-slate-700 p-2 rounded-md"/>
                </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button variant="primary" onClick={handleSave}>Set Date</Button>
            </div>
        </Modal>
    );
};
// --- Main View Component ---
export default function CampaignsView({ setActiveView }) {
    const [view, setView] = useState('dashboard');
    const [campaigns, setCampaigns] = useState([]);
    const [currentCampaign, setCurrentCampaign] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [modal, setModal] = useState({ isOpen: false, title: '', message: '' });
    const [confirmation, setConfirmation] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
    const [toastMessage, setToastMessage] = useState('');

    const fetchCampaigns = useCallback(async () => {
        setIsLoading(true);
        try {
            const campaignsData = await apiService.fetchCampaigns();
            setCampaigns(campaignsData);
        } catch (err) {
            console.error("Error fetching campaigns:", err);
            setModal({ isOpen: true, title: 'Error', message: 'Could not load campaigns.' });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCampaigns();
    }, [fetchCampaigns]);

    const handleCreateNew = () => {
        setCurrentCampaign(null);
        setView('builder');
    };

    const handleEdit = (campaign) => {
        setCurrentCampaign(campaign);
        setView('builder');
    };

    const handleDelete = (campaignId) => {
        setConfirmation({
            isOpen: true,
            title: 'Delete Campaign',
            message: 'Are you sure you want to delete this campaign? This cannot be undone.',
            onConfirm: async () => {
                try {
                    await apiService.deleteCampaign(campaignId);
                    setToastMessage('Campaign deleted successfully.');
                    await fetchCampaigns();
                } catch (error) {
                    setModal({ isOpen: true, title: 'Error', message: 'Failed to delete the campaign.' });
                }
                setConfirmation({ isOpen: false });
            }
        });
    };

    const handleExitBuilder = () => {
        setView('dashboard');
        fetchCampaigns();
    };

    const navItems = [
        { id: 'foundry', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'campaigns', label: 'Campaign Builder', icon: Layers },
        { id: 'foundryContentCreation', label: 'Content Creator', icon: PenSquare },
        { id: 'foundryCompetitorAnalysis', label: 'Competitor Analysis', icon: Users },
        { id: 'seoAudit', label: 'SEO Audit', icon: SearchCode },
    ];

    if (isLoading) {
        return <PageLoader text="Loading Campaigns..." />;
    }

    return (
        <div className="p-4 sm:p-6 md:p-8">
            <Toast message={toastMessage} onDone={() => setToastMessage('')} />

            <header className="flex flex-col md:flex-row justify-between md:items-center gap-4 border-b border-slate-800/50 pb-5 mb-8">
                <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">Foundry</h1>
                <div className="bg-slate-800/50 p-1 rounded-lg flex items-center gap-1 flex-wrap">
                    {navItems.map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveView(item.id)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors duration-200 ${
                                'campaigns' === item.id ? 'bg-brand-500/20 text-brand-300' : 'text-slate-300 hover:bg-slate-700/50'
                            }`}
                        >
                            <item.icon size={16} />
                            {item.label}
                        </button>
                    ))}
                </div>
            </header>
            
            {view === 'dashboard' ? (
                <CampaignDashboard
                    campaigns={campaigns}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onCreateNew={handleCreateNew}
                />
            ) : (
                <CampaignBuilder
                    initialCampaign={currentCampaign}
                    allCampaigns={campaigns} // Pass the full list of campaigns to the builder
                    onExit={handleExitBuilder}
                    setToastMessage={setToastMessage}
                    setConfirmation={setConfirmation}
                />
            )}
            
            {modal.isOpen && (
                <Modal isOpen={modal.isOpen} onClose={() => setModal({ isOpen: false })} title={modal.title}>
                    <p>{modal.message}</p>
                    <div className="mt-4 flex justify-end">
                        <Button onClick={() => setModal({ isOpen: false })}>Close</Button>
                    </div>
                </Modal>
            )}
            {confirmation.isOpen && (
                <ConfirmationModal 
                    isOpen={confirmation.isOpen}
                    onClose={() => setConfirmation({ isOpen: false })}
                    onConfirm={confirmation.onConfirm}
                    title={confirmation.title}
                    message={confirmation.message}
                />
            )}
        </div>
    );
}